import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../utils/analytics';
import { checkCookieSupport, AUTH_ERROR_MESSAGES, classifyAuthError } from '../utils/authDiagnostics';
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertTriangle, MailCheck } from 'lucide-react';
import { API } from '../utils/apiBase';
import Logo from '../components/Logo';


// Error formatting helper
const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [cookieWarning, setCookieWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, googleLogin, refreshAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'auth_failed') {
      const reason = params.get('reason');
      setError(AUTH_ERROR_MESSAGES[reason] || AUTH_ERROR_MESSAGES.auth_failed);
    }
    // Cookie-support probe — warns iOS Safari users with "Block All Cookies" etc.
    const cookieCheck = checkCookieSupport();
    if (!cookieCheck.ok) {
      setCookieWarning(AUTH_ERROR_MESSAGES[cookieCheck.reason] || AUTH_ERROR_MESSAGES.cookies_blocked);
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      analytics.login('email');
      navigate('/dashboard');
    } catch (err) {
      const reason = classifyAuthError(err);
      setError(
        (reason && AUTH_ERROR_MESSAGES[reason]) ||
          formatApiErrorDetail(err.response?.data?.detail) ||
          err.message
      );
    } finally {
      setLoading(false);
    }
  }, [email, password, login, navigate]);

  const togglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSentFor, setMagicSentFor] = useState('');
  const [magicError, setMagicError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const handleMagicLink = useCallback(async () => {
    setMagicError('');
    if (!email || !/.+@.+\..+/.test(email)) {
      setMagicError('Enter your email above, then click this again.');
      return;
    }
    setMagicLoading(true);
    // Retry once on transient 5xx / network errors — email delivery
    // occasionally has brief hiccups that resolve on immediate retry.
    const attempt = async () => axios.post(`${API}/auth/magic-link/request`, { email: email.trim().toLowerCase() });
    try {
      try {
        await attempt();
      } catch (err) {
        const status = err?.response?.status;
        const isTransient = !err?.response || status >= 500;
        if (!isTransient) throw err;
        // brief backoff, then one retry
        await new Promise((r) => setTimeout(r, 600));
        await attempt();
      }
      setMagicSentFor(email);
    } catch (err) {
      const reason = classifyAuthError(err);
      setMagicError((reason && AUTH_ERROR_MESSAGES[reason]) || 'Couldn\'t send the code. Try again in a moment.');
    } finally {
      setMagicLoading(false);
    }
  }, [email]);

  const handleOtpVerify = useCallback(async (e) => {
    e?.preventDefault?.();
    setError('');
    const cleaned = otpCode.replace(/\D/g, '');
    if (cleaned.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(
        `${API}/auth/otp/verify`,
        { email: (magicSentFor || email).trim().toLowerCase(), code: cleaned },
        { withCredentials: true }
      );
      // CRITICAL: refresh AuthContext so ProtectedRoute sees the user
      // before we navigate — otherwise /dashboard bounces back to /login.
      await refreshAuth();
      analytics.login('otp');
      navigate('/dashboard');
    } catch (err) {
      const reason = classifyAuthError(err);
      setError(
        (reason && AUTH_ERROR_MESSAGES[reason]) ||
          formatApiErrorDetail(err.response?.data?.detail) ||
          err.message
      );
    } finally {
      setOtpLoading(false);
    }
  }, [otpCode, magicSentFor, email, navigate, refreshAuth]);

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" aria-label="Ascend — Home" className="inline-block mb-6">
              <Logo size={56} priority />
            </Link>
            <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">
              Welcome back
            </h1>
            <p className="mt-2 text-[#4b5563]">
              Sign in to continue optimizing your resumes
            </p>
          </div>

          {cookieWarning && (
            <div
              className="mb-4 p-4 bg-[#fffbeb] border border-[#eab308] text-[#92400e] text-sm flex items-start gap-3"
              data-testid="login-cookie-warning"
            >
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <div>
                <strong className="font-semibold">Cookies are blocked on this browser.</strong>
                <div className="mt-1">{cookieWarning}</div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-[#fef2f2] border border-[#e11d48] text-[#e11d48] text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={togglePassword}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#0f1115]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-[#002fa7] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit"
            >
              <span>{loading ? 'Signing in...' : 'Sign In'}</span>
              {!loading && <ArrowRight size={20} />}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#e5e7eb]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#6b7280]">Or continue with</span>
              </div>
            </div>

            {magicSentFor ? (
              <div className="mt-6 space-y-4">
                <div
                  className="p-4 bg-[#ecfdf5] border border-[#10b981] text-[#065f46] text-sm flex items-start gap-3"
                  data-testid="magic-link-sent"
                >
                  <MailCheck size={18} className="shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-semibold">Check your email.</strong>
                    <div className="mt-1">
                      We sent a sign-in code and link to <strong>{magicSentFor}</strong>. Click the link OR enter the 6-digit code below. Expires in 15 minutes.
                    </div>
                  </div>
                </div>

                <form onSubmit={handleOtpVerify} className="space-y-3" data-testid="otp-form">
                  <label className="block text-sm font-medium text-[#0f1115]">6-digit code from your email</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
                      setOtpCode(pasted);
                    }}
                    placeholder="123456"
                    className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] text-center text-2xl font-mono tracking-[0.3em] focus:border-[#002fa7] focus:outline-none transition-colors"
                    data-testid="otp-input"
                    aria-label="6-digit sign-in code"
                  />
                  <button
                    type="submit"
                    disabled={otpLoading || otpCode.length !== 6}
                    className="w-full py-3 bg-[#002fa7] text-white font-semibold hover:bg-[#001f70] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="otp-submit"
                  >
                    {otpLoading ? 'Signing in\u2026' : 'Sign in with code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMagicSentFor(''); setOtpCode(''); }}
                    className="w-full text-sm text-[#6b7280] hover:text-[#0f1115] transition-colors"
                    data-testid="otp-resend"
                  >
                    Didn&apos;t receive it? Send a new code
                  </button>
                </form>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={magicLoading}
                className="mt-6 w-full flex items-center justify-center space-x-3 py-3 border border-[#002fa7] bg-white text-[#002fa7] hover:bg-[#002fa7] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="magic-link-button"
              >
                <MailCheck size={20} />
                <span className="font-medium">{magicLoading ? 'Sending code\u2026' : 'Email me a sign-in code'}</span>
              </button>
            )}

            {magicError && (
              <p className="mt-2 text-sm text-[#e11d48]" data-testid="magic-link-error">{magicError}</p>
            )}

            <button
              onClick={() => { analytics.login('google'); googleLogin(); }}
              className="mt-3 w-full flex items-center justify-center space-x-3 py-3 border border-[#e5e7eb] bg-white hover:bg-[#f4f5f7] transition-colors"
              data-testid="google-login"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-[#0f1115] font-medium">Continue with Google</span>
            </button>
          </div>

          <p className="mt-8 text-center text-[#6b7280]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#002fa7] font-medium hover:underline" data-testid="register-link">
              Sign up free
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="hidden lg:flex flex-1 bg-[#002fa7] items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="font-satoshi text-4xl font-black tracking-tight mb-6">
            Land more interviews with AI-optimized resumes
          </h2>
          <p className="text-[#a5b4fc] text-lg leading-relaxed">
            Join thousands of job seekers who have improved their interview rates by 3x with Ascend's ATS optimization.
          </p>
          <div className="mt-12 space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-white"></div>
              <span>AI-powered ATS scoring</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-white"></div>
              <span>Smart keyword matching</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-white"></div>
              <span>Continuous re-optimization</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
